/**
 * SQLite schema migrations for connector cache
 */

import type Database from "better-sqlite3";
import { createChildLogger } from "../utils/logger.js";

const migrationLogger = createChildLogger({ module: "migrations" });

/**
 * Migration definition
 */
interface Migration {
  version: number;
  name: string;
  up: (db: Database.Database) => void;
}

/**
 * All migrations in order
 */
const migrations: Migration[] = [
  {
    version: 1,
    name: "initial_schema",
    up: (db) => {
      // Connectors table - stores connector metadata
      db.exec(`
        CREATE TABLE IF NOT EXISTS connectors (
          id TEXT PRIMARY KEY,
          name TEXT NOT NULL,
          display_name TEXT NOT NULL,
          description TEXT,
          icon_uri TEXT,
          publisher TEXT,
          tier TEXT NOT NULL,
          is_custom_api INTEGER NOT NULL DEFAULT 0,
          capabilities TEXT,
          created_at TEXT NOT NULL DEFAULT (datetime('now')),
          updated_at TEXT NOT NULL DEFAULT (datetime('now'))
        )
      `);

      // Connector operations (actions/triggers)
      db.exec(`
        CREATE TABLE IF NOT EXISTS operations (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          connector_id TEXT NOT NULL,
          operation_id TEXT NOT NULL,
          type TEXT NOT NULL,
          method TEXT,
          path TEXT,
          summary TEXT,
          description TEXT,
          visibility TEXT,
          is_trigger INTEGER NOT NULL DEFAULT 0,
          schema_json TEXT,
          created_at TEXT NOT NULL DEFAULT (datetime('now')),
          updated_at TEXT NOT NULL DEFAULT (datetime('now')),
          FOREIGN KEY (connector_id) REFERENCES connectors(id) ON DELETE CASCADE,
          UNIQUE (connector_id, operation_id)
        )
      `);

      // Cache metadata
      db.exec(`
        CREATE TABLE IF NOT EXISTS cache_metadata (
          key TEXT PRIMARY KEY,
          value TEXT NOT NULL,
          updated_at TEXT NOT NULL DEFAULT (datetime('now'))
        )
      `);

      // Indexes for common queries
      db.exec(`
        CREATE INDEX IF NOT EXISTS idx_connectors_name ON connectors(name);
        CREATE INDEX IF NOT EXISTS idx_connectors_display_name ON connectors(display_name);
        CREATE INDEX IF NOT EXISTS idx_connectors_tier ON connectors(tier);
        CREATE INDEX IF NOT EXISTS idx_operations_connector ON operations(connector_id);
        CREATE INDEX IF NOT EXISTS idx_operations_type ON operations(type);
      `);

      // Full-text search for connectors
      db.exec(`
        CREATE VIRTUAL TABLE IF NOT EXISTS connectors_fts USING fts5(
          name,
          display_name,
          description,
          publisher,
          content='connectors',
          content_rowid='rowid'
        )
      `);

      // Triggers to keep FTS in sync
      db.exec(`
        CREATE TRIGGER IF NOT EXISTS connectors_ai AFTER INSERT ON connectors BEGIN
          INSERT INTO connectors_fts(rowid, name, display_name, description, publisher)
          VALUES (NEW.rowid, NEW.name, NEW.display_name, NEW.description, NEW.publisher);
        END;

        CREATE TRIGGER IF NOT EXISTS connectors_ad AFTER DELETE ON connectors BEGIN
          INSERT INTO connectors_fts(connectors_fts, rowid, name, display_name, description, publisher)
          VALUES ('delete', OLD.rowid, OLD.name, OLD.display_name, OLD.description, OLD.publisher);
        END;

        CREATE TRIGGER IF NOT EXISTS connectors_au AFTER UPDATE ON connectors BEGIN
          INSERT INTO connectors_fts(connectors_fts, rowid, name, display_name, description, publisher)
          VALUES ('delete', OLD.rowid, OLD.name, OLD.display_name, OLD.description, OLD.publisher);
          INSERT INTO connectors_fts(rowid, name, display_name, description, publisher)
          VALUES (NEW.rowid, NEW.name, NEW.display_name, NEW.description, NEW.publisher);
        END;
      `);
    },
  },
];

/**
 * Get current schema version
 */
function getSchemaVersion(db: Database.Database): number {
  try {
    const result = db.prepare(
      "SELECT value FROM cache_metadata WHERE key = 'schema_version'"
    ).get() as { value: string } | undefined;
    return result ? parseInt(result.value, 10) : 0;
  } catch {
    // Table doesn't exist yet
    return 0;
  }
}

/**
 * Set schema version
 */
function setSchemaVersion(db: Database.Database, version: number): void {
  db.prepare(`
    INSERT OR REPLACE INTO cache_metadata (key, value, updated_at)
    VALUES ('schema_version', ?, datetime('now'))
  `).run(String(version));
}

/**
 * Run all pending migrations
 */
export function runMigrations(db: Database.Database): void {
  const currentVersion = getSchemaVersion(db);
  const pendingMigrations = migrations.filter((m) => m.version > currentVersion);

  if (pendingMigrations.length === 0) {
    migrationLogger.debug({ currentVersion }, "Database schema is up to date");
    return;
  }

  migrationLogger.info(
    { currentVersion, targetVersion: migrations[migrations.length - 1]?.version },
    "Running database migrations"
  );

  db.exec("BEGIN TRANSACTION");

  try {
    for (const migration of pendingMigrations) {
      migrationLogger.debug(
        { version: migration.version, name: migration.name },
        "Applying migration"
      );
      migration.up(db);
      setSchemaVersion(db, migration.version);
    }

    db.exec("COMMIT");
    migrationLogger.info("Database migrations completed successfully");
  } catch (error) {
    db.exec("ROLLBACK");
    migrationLogger.error({ error }, "Migration failed, rolling back");
    throw error;
  }
}

/**
 * Get the latest schema version
 */
export function getLatestVersion(): number {
  return migrations[migrations.length - 1]?.version ?? 0;
}
